import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import { Icon, Loader } from 'react-inv-forms';
import isEmpty from 'lodash.isempty';

import { closeChangeLogModal } from '../../../redux/appConfig/actions';
import { getChangeLogData } from '../../../redux/appConfig/selectors';

import StringBuilder from '../../../helpers/StringBuilder';

import {
  StyledModal,
  StyledInput,
  ChangeLogContainer,
  ChangeLogItem,
  ChangeLogFormName,
  NoDataInfo,
} from './ChangeLogModal.style';

import changeLogIcon from '../../../images/changeLog.svg';

/**
 * Komponent modala historii zmian ocen
 *
 */
const ChangeLogModal = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const changeLogData = useSelector(getChangeLogData);
  const [filteredChangeLog, setFilteredChangeLog] = useState<Modal.ChangeLogItem[]>([]);

  useEffect(() => {
    if (!isEmpty(changeLogData)) {
      setFilteredChangeLog(changeLogData.data);
    }
  }, [changeLogData]);

  const filterChangeLog = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.currentTarget;

    if (!isEmpty(value)) {
      const regex = new RegExp(StringBuilder.escapeRegExp(value), 'i');

      const filteredResult = changeLogData.data.filter(
        log =>
          regex.test(log.ocena) ||
          regex.test(log.dataZmiany) ||
          regex.test(log.wpisujacyOceneNazwa) ||
          regex.test(log.nazwaFormyZajecTranslation),
      );

      setFilteredChangeLog(filteredResult);
    } else setFilteredChangeLog(changeLogData.data);
  };

  const findLastGrade = (currentLog: Modal.ChangeLogItem, logIndex: number): JSX.Element => {
    const lastGrade = filteredChangeLog.find(
      (log, index) => log.nazwaFormyZajecTranslation === currentLog.nazwaFormyZajecTranslation && index > logIndex,
    );

    return lastGrade ? (
      <>
        <strong>{lastGrade.ocena}</strong>
        {separateDate(lastGrade.dataOceny)}
      </>
    ) : (
      <strong>{'-'}</strong>
    );
  };

  const separateDate = (date: string): string => ` (${isEmpty(date) ? t('DATE_NOT_AVAILABLE') : date.split(' ')[0]})`;

  return (
    <StyledModal
      visible={changeLogData.opened}
      title={
        <span>
          <Icon path={changeLogIcon} />
          {t('CHANGELOG_MODAL_TITLE')}
        </span>
      }
      onCancel={() => dispatch(closeChangeLogModal())}
      width='55%'
      className='cyChangeLogModal'
      destroyOnClose
    >
      <StyledInput
        onChange={filterChangeLog}
        prefix={<SearchOutlined />}
        placeholder={t('START_TYPING_SHORT')}
        data-cy='inputChangeLogModal'
      />
      {changeLogData.loading ? (
        <Loader style={{ position: 'relative', top: '10%', left: '50%', fontSize: '24px' }} />
      ) : (
        <ChangeLogContainer>
          {!isEmpty(filteredChangeLog) ? (
            filteredChangeLog.map((log, index) => (
              <ChangeLogItem key={index}>
                {`${log.dataZmiany ?? t('DATE_NOT_AVAILABLE')} - `}
                <ChangeLogFormName>{log.nazwaFormyZajecTranslation}</ChangeLogFormName>
                {` ${t('OF')} `}
                {findLastGrade(log, index)}
                {` ${t('TO')} `}
                <strong>{log.ocena}</strong>
                {separateDate(log.dataOceny)}
                {` ${t('BY')} `}
                <strong>{log.wpisujacyOceneNazwa}</strong>
                {log.prowadzacyNazwa &&
                  log.wpisujacyOceneNazwa !== log.prowadzacyNazwa &&
                  ` ${t('ON_BEHALF_OF')} ${log.prowadzacyNazwa}`}
              </ChangeLogItem>
            ))
          ) : (
            <NoDataInfo>{t('NO_DATA')}</NoDataInfo>
          )}
        </ChangeLogContainer>
      )}
    </StyledModal>
  );
};

export default ChangeLogModal;
